import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { AnimatePresence, motion } from 'framer-motion';
import { CheckCircle2Icon, LockIcon, ShoppingCartIcon, TagIcon } from 'lucide-react';
import { CartLineItem } from '../components/cart/CartLineItem';
import { Breadcrumbs } from '../components/layout/Breadcrumbs';
import { useCart } from '../contexts/CartContext';
import { products } from '../data/products';
import { paymentMethods } from '../data/navigation';
import { formatPrice } from '../utils/currency';
const PROMO = {
  code: 'AMPRA10',
  rate: 0.1,
  max: 250
};
export function Cart() {
  const {
    items,
    subtotal,
    count,
    addItem,
    clearCart
  } = useCart();
  const [code, setCode] = useState('');
  const [applied, setApplied] = useState(false);
  const [promoError, setPromoError] = useState('');
  const [orderNo, setOrderNo] = useState<string | null>(null);
  const discount = applied ? Math.min(Math.round(subtotal * PROMO.rate * 100) / 100, PROMO.max) : 0;
  const total = Math.round((subtotal - discount) * 100) / 100;
  const vat = Math.round((total - total / 1.21) * 100) / 100;
  const inCart = new Set(items.map(i => i.product.id));
  const addOns = products.filter(p => p.category === 'accessories' && !inCart.has(p.id));
  const applyPromo = (e: React.FormEvent) => {
    e.preventDefault();
    if (code.trim().toUpperCase() === PROMO.code) {
      setApplied(true);
      setPromoError('');
    } else {
      setPromoError('This code isn’t valid. Try AMPRA10.');
    }
  };
  if (orderNo) {
    return <div className="mx-auto flex max-w-lg flex-col items-center px-4 py-24 text-center" data-id="element-604" data-mp="2x83ygtoorxpn">
        <motion.div initial={{
        scale: 0.96,
        opacity: 0
      }} animate={{
        scale: 1,
        opacity: 1
      }} transition={{
        duration: 0.24,
        ease: [0.23, 1, 0.32, 1]
      }} data-id="element-605" data-mp="2j8uk7zk1jdob">
          <CheckCircle2Icon className="h-12 w-12 text-success" aria-hidden="true" data-id="element-606" data-mp="17tfmuunx6jso" />
        </motion.div>
        <h1 className="mt-5 text-3xl font-bold tracking-tight text-ink" data-id="element-607" data-mp="1bw2b6lldtml4">Thanks for your order</h1>
        <p className="mt-3 text-sm text-muted" data-id="element-608" data-mp="3eir6ytl8eu7b">
          Order <span className="font-semibold text-ink" data-id="element-609" data-mp="3ekajc93rol3t">{orderNo}</span> is confirmed. You’ll get a tracking link by email as soon as it ships.
        </p>
        <Link to="/shop" className="mt-8 inline-flex h-11 items-center rounded-lg bg-ink px-5 text-sm font-semibold text-white hover:bg-ink-soft" data-id="element-610" data-mp="34rmnphoiwn6o">
          Continue shopping
        </Link>
      </div>;
  }
  if (items.length === 0) {
    return <div className="mx-auto flex max-w-lg flex-col items-center px-4 py-24 text-center" data-id="element-611" data-mp="2vpn9sy8t1z7y">
        <ShoppingCartIcon className="h-12 w-12 text-ink/20" aria-hidden="true" data-id="element-612" data-mp="2qupzzp3pblu0" />
        <h1 className="mt-5 text-3xl font-bold tracking-tight text-ink" data-id="element-613" data-mp="2lu2a56r7lb1d">Your cart is empty</h1>
        <p className="mt-3 text-sm text-muted" data-id="element-614" data-mp="2gw0gxt02jvc6">Find the right battery in our shop or let the advisor pick one for you.</p>
        <div className="mt-8 flex gap-3" data-id="element-615" data-mp="k030eptqj1qs">
          <Link to="/shop" className="inline-flex h-11 items-center rounded-lg bg-ink px-5 text-sm font-semibold text-white hover:bg-ink-soft" data-id="element-616" data-mp="f2ksn2rgeskd">
            Browse products
          </Link>
          <Link to="/#advisor" className="inline-flex h-11 items-center rounded-lg border border-line px-5 text-sm font-semibold text-ink hover:border-ink/40" data-id="element-617" data-mp="rlge3241y1gn">
            Battery advisor
          </Link>
        </div>
      </div>;
  }
  return <div className="mx-auto max-w-7xl px-4 pb-16 pt-5 sm:px-6 lg:px-8" data-id="element-618" data-mp="11aztfxsk3d6r">
      <Breadcrumbs items={[{
      label: 'Home',
      href: '/'
    }, {
      label: 'Cart'
    }]} data-id="element-619" data-mp="4mafgik1v4q2" />
      <h1 className="mt-4 text-[28px] font-bold tracking-tight text-ink sm:text-4xl" data-id="element-620" data-mp="8mar5vfrl1tf">
        Cart <span className="text-muted" data-id="element-621" data-mp="dm6urtkjur8g">({count})</span>
      </h1>

      <div className="mt-8 grid gap-10 lg:grid-cols-12" data-id="element-622" data-mp="do05bjya9vlq">
        <div className="lg:col-span-8" data-id="element-623" data-mp="ila0lg8ct1hh">
          <ul className="divide-y divide-line border-y border-line" data-id="element-624" data-mp="iixb8i09noue">
            {items.map(item => <CartLineItem key={item.key} item={item} size="lg" data-id="element-625" data-mp="1p36qz0a0pq7r" />)}
          </ul>

          {addOns.length > 0 && <section aria-labelledby="addons-heading" className="mt-10" data-id="element-626" data-mp="32wlrsxuvkg3n">
              <h2 id="addons-heading" className="text-base font-bold text-ink" data-id="element-627" data-mp="32yyibzep7ysb">
                Don’t forget
              </h2>
              <ul className="mt-4 grid gap-3 sm:grid-cols-2" data-id="element-628" data-mp="3byblkac8br4l">
                {addOns.map(p => <li key={p.id} className="flex items-center gap-3 rounded-xl border border-line p-3" data-id="element-629" data-mp="36y7aizu3bolr">
                    <img src={p.image} alt="" className="h-14 w-14 rounded-lg bg-surface object-cover" data-id="element-630" data-mp="3lv6fefxk53cj" />
                    <div className="min-w-0 flex-1" data-id="element-631" data-mp="3gvtva2y2madn">
                      <p className="truncate text-sm font-semibold text-ink" data-id="element-632" data-mp="3gv289gijhkyz">{p.name}</p>
                      <p className="text-sm text-muted" data-id="element-633" data-mp="3uqe1tkkyl5eb">{formatPrice(p.price)}</p>
                    </div>
                    <button type="button" onClick={() => addItem(p, undefined, 1, false)} className="h-9 shrink-0 rounded-lg border border-ink px-3 text-xs font-semibold text-ink transition-colors duration-150 hover:bg-ink hover:text-white" data-id="element-634" data-mp="3qp2clg5zym6n">
                      Add
                    </button>
                  </li>)}
              </ul>
            </section>}
        </div>

        <aside className="lg:col-span-4" aria-label="Order summary" data-id="element-635" data-mp="d7p1uooq79ud">
          <div className="rounded-2xl bg-surface p-6 lg:sticky lg:top-40" data-id="element-636" data-mp="i3e26u8kkurq">
            <h2 className="text-base font-bold text-ink" data-id="element-637" data-mp="4d0vswbbto4i">Order summary</h2>

            <form onSubmit={applyPromo} className="mt-5" data-id="element-638" data-mp="940vg9btykap">
              <label htmlFor="promo" className="text-xs font-medium text-muted" data-id="element-639" data-mp="95kci25b8zdj">
                Discount code
              </label>
              <div className="mt-1.5 flex gap-2" data-id="element-640" data-mp="3vjliy4y9gbw9">
                <div className="relative flex-1" data-id="element-641" data-mp="3vkdghlbf4jp6">
                  <TagIcon className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted" aria-hidden="true" data-id="element-642" data-mp="3hjst0buk5zpc" />
                  <input id="promo" value={code} onChange={e => setCode(e.target.value)} disabled={applied} placeholder="e.g. AMPRA10" aria-invalid={!!promoError} aria-describedby="promo-msg" className="h-10 w-full rounded-lg border border-line bg-white pl-9 pr-3 text-sm uppercase text-ink placeholder:normal-case placeholder:text-muted focus:border-ink focus:outline-none disabled:opacity-60" data-id="element-643" data-mp="3lkcs3kyozde4" />
                </div>
                <button type="submit" disabled={applied || !code} className="h-10 rounded-lg bg-ink px-4 text-sm font-semibold text-white hover:bg-ink-soft disabled:cursor-not-allowed disabled:opacity-40" data-id="element-644" data-mp="263hvxpyxq3xg">
                  Apply
                </button>
              </div>
              <p id="promo-msg" className="mt-1.5 min-h-[1rem] text-xs" aria-live="polite" data-id="element-645" data-mp="plvhjh34n3sm">
                {promoError && <span className="text-sale" data-id="element-646" data-mp="2x9ykhj8erc1n">{promoError}</span>}
                {applied && <span className="font-medium text-success" data-id="element-647" data-mp="2xbt3gs04cu33">AMPRA10 applied — 10% off (max €250)</span>}
              </p>
            </form>

            <dl className="mt-4 space-y-2.5 border-t border-line pt-4 text-sm" data-id="element-648" data-mp="36awnby741din">
              <div className="flex justify-between" data-id="element-649" data-mp="3b1d3hq2bfd8p">
                <dt className="text-muted" data-id="element-650" data-mp="3b24z30vwxayz">Subtotal</dt>
                <dd className="font-medium text-ink" data-id="element-651" data-mp="3g0q0v4qtc2pj">{formatPrice(subtotal)}</dd>
              </div>
              <AnimatePresence data-id="element-652" data-mp="3kzaq694j11z7">
                {applied && <motion.div initial={{
                opacity: 0,
                height: 0
              }} animate={{
                opacity: 1,
                height: 'auto'
              }} exit={{
                opacity: 0,
                height: 0
              }} transition={{
                duration: 0.2
              }} className="flex justify-between overflow-hidden" data-id="element-653" data-mp="3kxpz4thf8163">
                    <dt className="text-muted" data-id="element-654" data-mp="1zlofqwh8ma6u">Discount</dt>
                    <dd className="font-medium text-success" data-id="element-655" data-mp="1zoanox81irpl">−{formatPrice(discount)}</dd>
                  </motion.div>}
              </AnimatePresence>
              <div className="flex justify-between" data-id="element-656" data-mp="1bzdyy5qykym">
                <dt className="text-muted" data-id="element-657" data-mp="f45s6epcahvf">Shipping</dt>
                <dd className="font-medium text-success" data-id="element-658" data-mp="f3npp0c337f2">Free</dd>
              </div>
            </dl>
            <div className="mt-4 flex items-baseline justify-between border-t border-line pt-4" data-id="element-659" data-mp="a5m5ud6u342j">
              <span className="text-base font-semibold text-ink" data-id="element-660" data-mp="3ile8gu4f59o1">Total</span>
              <span className="text-[28px] font-bold leading-none tracking-tight text-ink" data-id="element-661" data-mp="3ekc98dai4xm2">{formatPrice(total)}</span>
            </div>
            <p className="mt-1 text-right text-xs text-muted" data-id="element-662" data-mp="3skd5tgjqhivq">Incl. {formatPrice(vat)} VAT</p>

            <button type="button" onClick={() => {
            setOrderNo(`AMP-${Math.floor(100000 + Math.random() * 900000)}`);
            clearCart();
          }} className="mt-5 flex h-12 w-full items-center justify-center gap-2 rounded-lg bg-volt text-sm font-bold text-ink transition-colors duration-150 hover:bg-volt-hover" data-id="element-663" data-mp="3ninx47jzhhat">
              <LockIcon className="h-4 w-4" aria-hidden="true" data-id="element-664" data-mp="2prr9icm8gyxw" />
              Checkout securely
            </button>
            <p className="mt-3 text-center text-xs text-muted" data-id="element-665" data-mp="2bt1d6z78n3al">Delivered within 1–2 business days</p>
            <ul className="mt-5 flex flex-wrap justify-center gap-1.5" aria-label="Payment methods" data-id="element-666" data-mp="2grmaoqv3v8vc">
              {paymentMethods.map(m => <li key={m} className="rounded border border-line bg-white px-2 py-0.5 text-[11px] font-bold text-ink" data-id="element-667" data-mp="38fw8l6ry3vqd">
                  {m}
                </li>)}
            </ul>
          </div>
        </aside>
      </div>
    </div>;
}